import { AxiosError } from 'axios'
import * as yup from 'yup'
import type { Dispatch } from 'redux'
import api, {
  AccountAuthorizationBody,
  ApplicationNotFoundError,
  AuthorizationBody,
  ConsentBody,
  RejectionBody,
  RetailAuthenticationBody,
  RetailFetchTokenBody,
  RetailVerificationBody,
} from 'services/api'
import { FormikActions } from 'formik'
import {
  setAccessTokenAction,
  setActiveScreenAction,
  setApplicationDataAction,
  setApplicationDataFetchFailedAction,
  setApplicationDataFetchingAction,
  setAuthCheckingAction,
  setConsentAction,
  setConsentFetchingAction,
  setErrorAction,
  setPushNotificationTokenAction,
  setPushNotificationTokenFetchingAction,
} from 'reducers/generalReducer'
import { setPocketsAction, setPocketsFetchingAction } from 'reducers/pocketsReducer'
import { AccessToken, Error } from 'types/common'
import { Screens, Consents, LocalStorageKeys, FetchStates } from 'constants/common'
import type { AppState } from 'store'
import {
  setToLocalStorage,
  getFromLocalStorage,
  removeTokensFromStorage,
} from 'helpers/common'
import { ApiResponseError, ErrorDTO } from '../errors'

const authorizationScreens = {
  [Consents.ACCOUNT]: Screens.POCKETS,
  [Consents.PAYMENT]: Screens.PAYMENT,
  [Consents.FUNDS_CONFIRMATION]: Screens.FUNDS_CONFIRMATION,
}

const BAD_REQUEST_CODE = 400
const UNAUTHORIZED_CODE = 401
const UNPROCESSABLE_ENTITY_CODE = 422

const UNSUPPORTED_APP_VERSION_ERRORCODE = 2000
const TOO_MANY_ATTEMPTS_ERRORCODE = 9205

export type PasswordError = {
  message: string
  time?: number
}

const getErrorMessage = (
  statusCode: number,
  responseData?: ErrorDTO,
  appName?: string,
): Error => {
  if (responseData && responseData.allowedCountries !== undefined) {
    // Non standard error object
    return {
      message: 'Unfortunately your country is not supported',
      description: `Due to local financial regulation you are not currently able to grant consent to ${
        appName || 'this application'
      } in your country.`,
    }
  }
  const fullMessage =
    responseData &&
    responseData.Errors &&
    responseData.Errors.length > 0 &&
    responseData.Errors[0] &&
    responseData.Errors[0].Message
  const generalMessage = responseData && responseData.Message
  const genericMessage = 'Something went wrong'
  if (statusCode === UNAUTHORIZED_CODE) {
    return {
      message: fullMessage || generalMessage || 'Session expired, please login again',
    }
  }
  return { message: fullMessage || generalMessage || genericMessage }
}

const getApplicationDataError = (error: unknown): Error => {
  if (error instanceof ApplicationNotFoundError) {
    return { message: error.message, description: error.description }
  }

  if (error instanceof ApiResponseError) {
    return getErrorMessage(error.httpStatusCode, error.errorDto)
  }

  return { message: 'Something went wrong' }
}

export const verifyAuthAction =
  (data: RetailVerificationBody) => async (dispatch: Dispatch) => {
    try {
      const { access_token } = await api.verifyRetailUser(data)
      setToLocalStorage(LocalStorageKeys.ACCESS_TOKEN, access_token)
      setToLocalStorage(LocalStorageKeys.VERIFICATION_TOKEN, data.verify_token)
      dispatch(setAccessTokenAction(access_token))
    } catch (e: any) {
      dispatch(setActiveScreenAction(Screens.ERROR))
      dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data)))
    }
  }

export const fetchPushNotificationTokenAction =
  (data: RetailFetchTokenBody) => async (dispatch: Dispatch) => {
    dispatch(setPushNotificationTokenFetchingAction(FetchStates.PENDING))

    try {
      const { accessToken } = await api.fetchPushNotificationTokenForRetailUser(data)

      setToLocalStorage(LocalStorageKeys.ACCESS_TOKEN, accessToken)

      dispatch(setPushNotificationTokenFetchingAction(FetchStates.SUCCESS))
      dispatch(setAccessTokenAction(accessToken))
    } catch (e: any) {
      dispatch(setPushNotificationTokenFetchingAction(FetchStates.FAILURE))

      if (e.response.status === UNPROCESSABLE_ENTITY_CODE) {
        return
      }

      dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data)))
      dispatch(setActiveScreenAction(Screens.ERROR))
    }
  }

export const authorizeConsentAction =
  (data: AccountAuthorizationBody | AuthorizationBody) =>
  async (dispatch: Dispatch, getState: () => AppState) => {
    const {
      general: {
        data: {
          accessToken,
          consent: { type = Consents.ACCOUNT },
        },
      },
    } = getState()
    try {
      const { redirect_uri } = await api.authorize(data, accessToken, type)
      dispatch(setActiveScreenAction(Screens.SUCCESS))
      setTimeout(() => {
        window.location.href = redirect_uri
      }, 3000)
    } catch (e: any) {
      dispatch(setActiveScreenAction(Screens.ERROR))
      dispatch(
        dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data))),
      )
    } finally {
      removeTokensFromStorage()
    }
  }

export const rejectConsentAction =
  (data: RejectionBody) => async (dispatch: Dispatch, getState: () => AppState) => {
    const {
      general: {
        data: {
          accessToken,
          consent: { type = Consents.ACCOUNT },
        },
      },
    } = getState()
    try {
      const { redirect_uri } = await api.reject(data, accessToken, type)
      dispatch(setActiveScreenAction(Screens.SUCCESS_REJECT))
      setTimeout(() => {
        window.location.href = redirect_uri
      }, 3000)
    } catch (e: any) {
      dispatch(setActiveScreenAction(Screens.ERROR))
      dispatch(
        dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data))),
      )
    } finally {
      removeTokensFromStorage()
    }
  }

export const fetchPocketsAction =
  (accessToken: AccessToken) => async (dispatch: Dispatch) => {
    dispatch(setPocketsFetchingAction())
    try {
      const pockets = await api.fetchPockets(accessToken)
      dispatch(setPocketsAction(pockets))
    } catch (e: any) {
      dispatch(setActiveScreenAction(Screens.ERROR))
      removeTokensFromStorage()
      dispatch(
        dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data))),
      )
    }
  }

type SlideFormikActions = Pick<
  FormikActions<any>,
  'setFieldValue' | 'setErrors' | 'setSubmitting'
>

const httpBadRequestPayloadSchema = yup.object({ Code: yup.number() })

const isMobileConsentUnavailable = (e: AxiosError) =>
  e.response &&
  e.response.status === BAD_REQUEST_CODE &&
  httpBadRequestPayloadSchema.isValidSync(e.response.data) &&
  e.response.data.Code === UNSUPPORTED_APP_VERSION_ERRORCODE

export const authenticateAction =
  (data: RetailAuthenticationBody, slideFormikActions: SlideFormikActions) =>
  async (dispatch: Dispatch<any>) => {
    try {
      const { headers, data: axiosData } = await api.authenticateRetailUser(data)
      const devCode = headers['x-dev-code'] || ''

      slideFormikActions.setSubmitting(false)
      slideFormikActions.setFieldValue('code', devCode)

      if (data.type === 'APP' && axiosData) {
        dispatch(setPushNotificationTokenAction(axiosData))
        dispatch(setActiveScreenAction(Screens.PUSH_NOTIFICATION))

        return
      }

      dispatch(setActiveScreenAction(Screens.CONFIRMATION))
    } catch (e: any) {
      slideFormikActions.setSubmitting(false)

      if (e.response.status === UNAUTHORIZED_CODE) {
        slideFormikActions.setErrors({
          password: { message: 'Invalid credentials' },
        })

        return
      }

      if (
        e.response.status === UNPROCESSABLE_ENTITY_CODE &&
        e.response.data.Code === TOO_MANY_ATTEMPTS_ERRORCODE
      ) {
        slideFormikActions.setErrors({
          password: {
            message: 'Too many attempts. Please wait for {time}',
            time: e.response.headers['retry-after'],
          },
        })

        return
      }

      if (isMobileConsentUnavailable(e)) {
        dispatch(
          authenticateAction(
            {
              ...data,
              type: 'SMS',
            },
            slideFormikActions,
          ),
        )

        return
      }

      dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data)))
      dispatch(setActiveScreenAction(Screens.ERROR))
    }
  }

export const fetchApplicationDataAction =
  (clientId: string) => async (dispatch: Dispatch) => {
    dispatch(setApplicationDataFetchingAction())
    try {
      const appData = await api.fetchAppData({ clientId })
      dispatch(setApplicationDataAction(appData))
    } catch (error: unknown) {
      dispatch(setApplicationDataFetchFailedAction(getApplicationDataError(error)))
    }
  }

export const checkAuthAction = () => async (dispatch: Dispatch) => {
  try {
    const accessTokenFromStorage = getFromLocalStorage(LocalStorageKeys.ACCESS_TOKEN)
    if (
      getFromLocalStorage(LocalStorageKeys.VERIFICATION_TOKEN) &&
      accessTokenFromStorage
    ) {
      dispatch(setAuthCheckingAction(FetchStates.PENDING))
      try {
        // Test fetch to make sure credentials are still valid
        // TODO: Replace with call to some test endpoint
        await api.fetchPockets(accessTokenFromStorage)
        dispatch(setAccessTokenAction(getFromLocalStorage(LocalStorageKeys.ACCESS_TOKEN)))
        dispatch(setAuthCheckingAction(FetchStates.SUCCESS))
      } catch (e: any) {
        if (e.response.status !== UNAUTHORIZED_CODE) {
          // Rethrow to show error screen etc.
          throw e
        } else {
          // Clear tokens if test wasn't successful
          removeTokensFromStorage()
          dispatch(setAuthCheckingAction(FetchStates.FAILURE))
        }
      }
    }
  } catch (e: any) {
    dispatch(setActiveScreenAction(Screens.ERROR))
    dispatch(
      dispatch(setErrorAction(getErrorMessage(e.response.status, e.response.data))),
    )
  }
}

export const fetchConsentDetailsAction =
  (data: ConsentBody, accessToken: AccessToken) =>
  async (dispatch: Dispatch, getState: () => AppState) => {
    const {
      general: {
        data: { applicationData },
      },
    } = getState()
    const appName =
      applicationData.fetchState === FetchStates.SUCCESS
        ? applicationData.data.name
        : undefined
    try {
      dispatch(setConsentFetchingAction())
      const consent = await api.fetchConsentDetails(data, accessToken)
      dispatch(setConsentAction(consent))
      const screen = consent.type
        ? authorizationScreens[consent.type]
        : Screens.CREDENTIALS
      dispatch(setActiveScreenAction(screen))
    } catch (e: any) {
      dispatch(setActiveScreenAction(Screens.ERROR))
      dispatch(
        dispatch(
          setErrorAction(getErrorMessage(e.response.status, e.response.data, appName)),
        ),
      )
    }
  }
